import dayjs from "dayjs";
import { campoDeLaCaja, comportamientoDelPais } from "../config/integracionPorPais";
import {
  cajeroDelDocumentoBC,
  comprobanteCorregido,
  comprobanteRelacionado,
  documentoAlQueSeAplica,
  horaDelDocumento,
  primerValor,
  tipoDeDocumentoBC,
  CLAVES,
} from "./camposDeBusinessCentral";
import { fetchFromBusinessCentral } from "../utils/businessCentralUtils";
import db from "../db/database";
import { resolverTipoDeDocumento } from "../constants/identityDocuments";
import { aHoraLocalDelTerminal } from "./corteDeHoraDeBC";
import { tieneFormaDeSello } from "../services/selloDeDocumentoBorrado";
import { camposDeExclusion } from "../services/abonoPermitido";
import { anotarAviso } from "../services/syncLog";
import { COUNTRY_CODES } from "../constants/countries";
import { MOTIVOS_NOTA_CREDITO_PERU } from "../constants/creditMemoReasons";
import { camposFiscalesDelDocumento } from "../components/Invoice/extensions/panama/datosFiscalesDePanama";

/**
 * Tiquete o factura, tal como lo publica Business Central.
 *
 * **En Panamá no existe esa distinción**: al consumidor final también se le hace
 * factura, y lo que cambia es el tipo de receptor. Pero Panamá comparte ambiente
 * con Costa Rica, así que su empresa tiene igualmente el campo de la migración
 * costarricense, y viene relleno: medido el 17/09/2026 en PAN DEV, las 3.000
 * facturas registradas más recientes traen `EXCCRIElectronicDocumentType` =
 * «Factura», un valor por defecto que allá no significa nada. Importarlo sería
 * inventarle al documento un tipo de comprobante que su país no tiene.
 */
const tipoDeComprobanteDeBC = (apiObj, countryCode) => {
  if (!comportamientoDelPais(countryCode, "importaTipoDeComprobante")) return undefined;
  return comprobanteCorregido(apiObj) ?? apiObj.EXCCRIElectronicDocumentType ?? undefined;
};

// Builders
export function buildSyncMessage(
  invoiceCount,
  creditMemoCount,
  postedInvoiceCount,
  postedCreditMemosCount,
  cierreCount = 0,
  cierresEncontrados = 0,
  aperturaCount = 0,
  aperturasEncontradas = 0
) {
  if (
    invoiceCount +
      creditMemoCount +
      postedInvoiceCount +
      postedCreditMemosCount +
      cierreCount +
      cierresEncontrados +
      aperturaCount +
      aperturasEncontradas ===
    0
  ) {
    return "No se encontraron documentos históricos para los filtros seleccionados.";
  }

  let parts = [];

  if (invoiceCount > 0) {
    parts.push(
      invoiceCount === 1
        ? "1 factura fue sincronizada correctamente"
        : `${invoiceCount} facturas fueron sincronizadas correctamente`
    );
  }

  if (creditMemoCount > 0) {
    parts.push(
      creditMemoCount === 1
        ? "1 nota de crédito fue sincronizada correctamente"
        : `${creditMemoCount} notas de crédito fueron sincronizadas correctamente`
    );
  }

  if (postedInvoiceCount > 0) {
    parts.push(
      postedInvoiceCount === 1
        ? "1 factura contabilizada fue sincronizada correctamente"
        : `${postedInvoiceCount} facturas contabilizadas fueron sincronizadas correctamente`
    );
  }

  if (postedCreditMemosCount > 0) {
    parts.push(
      postedCreditMemosCount === 1
        ? "1 nota de crédito contabilizada fue sincronizada correctamente"
        : `${postedCreditMemosCount} notas de crédito contabilizadas fueron sincronizadas correctamente`
    );
  }

  if (cierreCount > 0) {
    parts.push(
      cierreCount === 1
        ? "1 cierre de caja fue importado correctamente"
        : `${cierreCount} cierres de caja fueron importados correctamente`
    );
  } else if (cierresEncontrados > 0) {
    // BC sí tiene cierres para la caja/rango consultado, pero ya estaban
    // todos importados localmente (corrida repetida) -- sin esto el
    // mensaje no menciona los cierres en absoluto, dando la impresión de
    // que la importación no funcionó.
    parts.push(
      cierresEncontrados === 1
        ? "1 cierre de caja consultado ya estaba importado localmente"
        : `${cierresEncontrados} cierres de caja consultados ya estaban importados localmente`
    );
  }

  if (aperturaCount > 0) {
    parts.push(
      aperturaCount === 1
        ? "1 apertura de caja fue importada correctamente"
        : `${aperturaCount} aperturas de caja fueron importadas correctamente`
    );
  } else if (aperturasEncontradas > 0) {
    parts.push(
      aperturasEncontradas === 1
        ? "1 apertura de caja consultada ya estaba importada localmente"
        : `${aperturasEncontradas} aperturas de caja consultadas ya estaban importadas localmente`
    );
  }

  return parts.join("\n");
}
/**
 * El `$filter` de Traer Históricos.
 *
 * El turno viaja **sólo a los borradores**. Las registradas no publican `Turno`
 * --Costa Rica lo publica como `TurnoTPV`, y no todos los ambientes lo tienen--,
 * y un filtro sobre una propiedad que la entidad no publica no devuelve cero
 * filas: tumba la consulta entera con un 400. Medido el 12/09/2026 en Costa Rica
 * DEV, TEST y producción: «Could not find a property named 'Turno' on type
 * 'NAV.PostedSalesInvoice'», y con eso todo Traer Históricos en cuanto el cajero
 * elegía un turno.
 *
 * Las registradas se filtran por turno en el terminal, con `delTurno`, sobre el
 * turno ya mapeado, que lee los dos nombres. Acuerdo
 * `contrato-hallazgos-ciclo-cri-2026-09-12.md`, H2.
 */
export function buildFilters(values, isPosted, countryCode) {
  const fechaInicio = dayjs(values.FechaInicio).format("YYYY-MM-DD");
  const fechaFin = dayjs(values.FechaFin).format("YYYY-MM-DD");

  // En las entidades contabilizadas el campo que trae la caja se llama «TPV» en
  // todos los países salvo en Perú, que lo publica como «Caja»; en los
  // borradores se llama «Caja» en los nueve. La misma regla estaba escrita
  // también en `invoiceSyncService`, con un comentario diciéndolo.
  const campoCaja = campoDeLaCaja(countryCode, isPosted);

  const filters = [
    `Tienda eq '${values.TPV}'`,
    `${campoCaja} eq '${values.Caja}'`,
    `(Posting_Date ge ${fechaInicio} and Posting_Date le ${fechaFin})`,
  ];

  if (values.Turno && !isPosted) {
    filters.push(`Turno eq ${values.Turno}`);
  }

  return `$filter=${filters.join(" and ")}`;
}

/**
 * Los documentos registrados del turno pedido, filtrados en el terminal.
 *
 * Un documento que no trae turno **se conserva**: no se puede saber de qué turno
 * es, y descartarlo lo haría desaparecer del cierre sin rastro. Se deja escrito
 * en la consola cuántos entraron así.
 *
 * @param {Array} documentos Documentos ya mapeados, con `turno`.
 * @param {number|string} turno El turno elegido en el formulario, o nada.
 * @param {string} entidad Cómo se llaman en la consola de sincronización.
 */
export const delTurno = (documentos, turno, entidad = "facturas") => {
  const lista = Array.isArray(documentos) ? documentos : [];
  if (turno === undefined || turno === null || String(turno).trim() === "") return lista;

  let sinTurno = 0;
  const delPedido = lista.filter((documento) => {
    const suyo = documento?.turno;
    if (suyo === undefined || suyo === null || String(suyo).trim() === "") {
      sinTurno += 1;
      return true;
    }
    return String(suyo).trim() === String(turno).trim();
  });

  if (sinTurno > 0) {
    anotarAviso(
      entidad,
      `${sinTurno} documento(s) registrado(s) sin turno en Business Central: se traen igual, porque no se puede saber de qué turno son`
    );
  }

  return delPedido;
};

const mapDireccion2FromApi = (apiObj) =>
  apiObj.Bill_to_Address_2 ?? apiObj.Sell_to_Address_2 ?? apiObj.Direccion_2 ?? "";
// Mapping: API → IndexedDB schema
/**
 * La fecha del documento **con su hora**, para poder ubicarlo dentro de un turno.
 *
 * `lastModified` es lo que usan los filtros por rango --el arqueo del cierre
 * (invoicePaymentService) y el listado por rango (invoiceService)--, y hasta
 * ahora guardaba `Posting_Date` a secas: una fecha sin hora, que dayjs
 * interpreta como medianoche.
 *
 * Con eso, **ninguna factura importada entraba nunca en el rango de un cierre**:
 * la apertura del 26/08 fue a las 12:01 y todos sus documentos quedaban en las
 * 00:00, o sea antes del inicio del turno. El detalle del cierre salía vacío
 * aunque las facturas estuvieran ahí.
 *
 * `PostedSalesInvoice` sí publica la hora en `Hora_creacion` (medido:
 * "18:03:34.293"). Se pega a la fecha sin zona horaria a propósito: el rango
 * del cierre se arma con la `HoraDeApertura` local, así que los dos lados
 * tienen que hablar en hora local.
 *
 * Desde el bloque 3 del acuerdo 11 las entidades sin contabilizar también la
 * publican -- comprobado contra el ambiente sobre un borrador recién creado--,
 * así que los cuatro orígenes traen hora. Si alguna vez faltara, se conserva la
 * fecha sola y ese documento queda fuera de todo turno que no empiece a
 * medianoche.
 *
 * El huso ya cuadra, con una corrección para lo viejo. Hasta la extensión
 * 1.0.4.57 Business Central estampaba `Hora_creacion` con su reloj en UTC,
 * mientras que `HoraDeApertura` viaja en hora local del terminal: los
 * documentos anteriores a ese corte caen cinco horas más tarde de lo que
 * ocurrieron y se cuelan en el turno siguiente. Medido en vivo el 27/08/2026:
 * los tres documentos de las 05:04-05:12 aparecieron dentro de un turno que
 * abrió a las 06:51.
 *
 * `corte` es el instante a partir del cual BC respeta la hora que manda el
 * terminal, y viene de utils/corteDeHoraDeBC.js, donde está documentado cómo se
 * midió. Sin corte no se corrige nada: es el comportamiento de antes.
 *
 * @param {Object} apiObj    Documento tal como lo devuelve Business Central.
 * @param {number|null} corte Instante del corte en milisegundos.
 */
/**
 * La hora del alta en hora del terminal, `HH:mm:ss`, o cadena vacía.
 *
 * Es la misma que coloca el documento en su turno (`fechaHoraDelDocumento`).
 * Antes se guardaba la cruda: en Perú llega en UTC y con milésimas
 * («01:32:56.773»), y el tiquete, que sólo acepta `HH:mm:ss`, imprimía
 * «00:00:00». Medido en PER TEST el 29/10/2026 con B4B1-0000056.
 */
export const horaLocalDelDocumento = (apiObj, corte = null) => {
  const fechaHora = fechaHoraDelDocumento(apiObj, corte);
  const hora = /T(\d{2}:\d{2}(?::\d{2})?)/.exec(fechaHora || "");
  if (!hora) return "";
  return hora[1].length === 5 ? `${hora[1]}:00` : hora[1];
};

export const fechaHoraDelDocumento = (apiObj, corte = null) => {
  const fecha = apiObj?.Posting_Date ?? apiObj?.Document_Date ?? "";
  if (!fecha) return "";

  // El nombre del campo cambia con el país: ver utils/camposDeBusinessCentral.js.
  // Leer sólo `Hora_creacion` dejaba a Costa Rica sin hora, y un documento sin
  // hora cae a medianoche --o sea, fuera del rango de cualquier turno--.
  const hora = horaDelDocumento(apiObj);
  if (typeof hora !== "string" || !/^\d{1,2}:\d{2}/.test(hora)) return fecha;

  return aHoraLocalDelTerminal(apiObj, `${fecha}T${hora}`, corte);
};

/**
 * Si el documento es un abono.
 *
 * Hace falta porque los dos mapeadores sirven a las dos entidades, y el enlace
 * con la factura corregida **sólo tiene sentido en un abono**. Los campos de
 * respaldo de ese enlace vienen rellenos también en una factura, con su propio
 * número del sistema anterior, así que aplicarlos sin distinguir dejaba a cada
 * factura traída apuntándose a sí misma.
 */
const esUnAbono = (documentType, apiObj) => {
  // Por la cadena de candidatos, no por `Document_Type` a secas: `SalesCreditMemo`
  // no publica ese nombre --lo llama `TipoDocumento`--, así que leerlo directo
  // daba `undefined` en TODA nota de crédito sin contabilizar. Y el daño no era
  // sólo el tipo: con esto en falso se apagaba el respaldo del enlace con la
  // factura corregida, que es justo el campo que esas filas sí traen.
  const tipo = String(documentType ?? tipoDeDocumentoBC(apiObj) ?? "").toLowerCase();
  return tipo.includes("credit") || tipo.includes("memo") || tipo.includes("abono");
};

/**
 * El tipo con el que el terminal guarda una venta traída de Business Central.
 *
 * En BC la boleta de Perú es una factura más («Invoice»): lo que la distingue es
 * `SUNAT_Document` «03». El terminal, en cambio, guarda la boleta que hace él
 * mismo como «Boleta», y de eso depende el rótulo del listado. Sin esta
 * traducción, toda boleta que volvía por «Traer históricos» se leía «Factura»
 * (medido en PER TEST el 29/10/2026 con B4B1-0000056 y 0057). Mismo criterio que
 * `tipoDeDocumentoDeBC` del informe de tiendas.
 *
 * Sólo se toca la factura de Perú con «03»: un abono, una factura «01» o
 * cualquier otro país salen como vinieron.
 */
export const tipoLocalDeLaVenta = (tipo, apiObj, countryCode) =>
  countryCode === COUNTRY_CODES.PERU &&
  tipo === "Invoice" &&
  String(apiObj?.SUNAT_Document ?? "").trim() === "03"
    ? "Boleta"
    : tipo;

/**
 * El motivo y el sustento de una nota de crédito traída de Business Central.
 *
 * El terminal los manda como `Cod_Tipo_Nota_Credito` y `Sustento_Nota_Credito`,
 * pero Perú (`EnlacePos-Peru`) los publica con otros nombres:
 * `Tipo_Motivo_NC` (o `Motivo_correccion`) y `Sustento_Motivo_NC_o_ND`. Sin
 * leerlos, toda nota de crédito traída de históricos imprimía el motivo y el
 * sustento por defecto --«06 Devolución total»-- aunque fuera una devolución
 * por ítem. Medido en PER TEST el 29/10/2026 con B4N1-0000006 (motivo 07).
 *
 * El nombre del motivo no viaja: se deduce del código con el catálogo SUNAT.
 */
export const camposDeLaNotaDeCredito = (apiObj) => {
  const codigo =
    primerValor(apiObj, ["Cod_Tipo_Nota_Credito", "Tipo_Motivo_NC", "Motivo_correccion"]) ?? "";
  const nombre =
    primerValor(apiObj, ["Tipo_Nota_Credito"]) ??
    MOTIVOS_NOTA_CREDITO_PERU.find((m) => m.value === String(codigo).trim())?.label ??
    "";
  return {
    Cod_Tipo_Nota_Credito: String(codigo),
    Tipo_Nota_Credito: nombre,
    Sustento_Nota_Credito:
      primerValor(apiObj, ["Sustento_Nota_Credito", "Sustento_Motivo_NC_o_ND"]) ?? "",
  };
};

export const mapInvoice = (apiObj, countryCode, corte = null) => {
  return {
    systemId: apiObj.systemId ?? apiObj.SystemId ?? "",
    No: "", //Factura no tiene 'No final' hasta contabilizar
    Pre_Assigned_No: apiObj.No ?? "",
    Id_TPV: apiObj.Id_TPV ?? "",
    // Por la cadena de candidatos: ver `CLAVES.tipoDeDocumento`. Su hermana
    // `mapPostedInvoice` recibe el tipo por parámetro y por eso nunca se notó.
    Document_Type: tipoLocalDeLaVenta(tipoDeDocumentoBC(apiObj) ?? "", apiObj, countryCode),
    // Factura (01) o boleta (03) en Perú, tal como lo publica Business Central.
    // Con su propio nombre: `SUNAT_Document` en el documento del terminal es el
    // tipo de identidad del cliente. Ver utils/tipoSunatDelDocumento.js.
    Tipo_SUNAT: apiObj.SUNAT_Document || undefined,
    Sell_to_Customer_No_: apiObj.Sell_to_Customer_No ?? "",
    Sell_to_Customer_Name_2: apiObj.Sell_to_Customer_Name_2 ?? "",
    // `undefined` y no cadena vacía, igual que `Dto` e `importeDto`.
    //
    // Que el campo llegue o no depende de la extensión que tenga instalada el
    // ambiente, no del país. Lo declara `ExceliaOne BC - E2` --campo 75200 en
    // `Sales Header`, `Sales Invoice Header` y `Sales Cr.Memo Header`-- y lo
    // publica en `FacturasAPI`, `PostedSalesInvoices` y
    // `PostedSalesCreditMemos`. Donde corre el núcleo `ExceliaOne BC` el campo
    // no existe, y entonces las cuatro entidades no lo publican: medido el
    // 16/10/2026 en Corporativo_Santillana_2_Dev, que es Costa Rica, Panamá y
    // Puerto Rico.
    //
    // En ese segundo caso `?? ""` lo BORRABA en cada fila que reemplaza Traer
    // Históricos, y lo borraba de verdad: lo teclea el cajero --formulario de El
    // Salvador y de Honduras, donde puede ser obligatorio-- y el ambiente no
    // tiene de dónde devolverlo. Con `undefined` la fila conserva la suya.
    //
    // Sí viaja a Business Central: `invoiceSyncService` arma la cabecera con
    // `...invoice` y no está en la lista de `camposLocalesDeLaFactura`. Donde
    // está E2 se guarda --`Cod90100` lo lee de `Razon_Social`-- y sobrevive al
    // contabilizar porque es el mismo número de campo en las tres tablas.
    Razon_Social: apiObj.Razon_Social ?? undefined,
    Posting_No: apiObj.External_Document_No ?? "",
    draftNo: "",
    Colegio: apiObj.Cod_Colegio ?? "",
    Bill_to_Name: apiObj.Bill_to_Name ?? "",
    Bill_to_Address: apiObj.Bill_to_Address ?? "",
    Direccion_2: mapDireccion2FromApi(apiObj),
    Bill_to_City: apiObj.Bill_to_City ?? "",
    VAT_Registration_No_: apiObj.VAT_Registration_No ?? "",
    Bill_to_Country_Region_Code: apiObj.Bill_to_Country_Region_Code ?? "",
    // La ubicación del comprador en Costa Rica. Con `?? undefined`: donde BC no
    // la publique, no se pisa la copia local. Contrato de ubicación, fila T6.
    ...(countryCode === COUNTRY_CODES.COSTARICA
      ? {
          Sell_to_Post_Code: apiObj.Sell_to_Post_Code ?? undefined,
          Sell_to_Country_Region_Code: apiObj.Sell_to_Country_Region_Code ?? undefined,
        }
      : {}),
    Ship_to_Code: apiObj.Ship_to_Code ?? "",
    Tipo_Documento_SrI: resolverTipoDeDocumento(apiObj, countryCode, "factura"),
    // En Costa Rica, primero el correo del comprobante (`E-Mail-FE`): las
    // registradas lo publican como `EXCCRIElectronicInvoiceEmail` y los abonos sin
    // registrar como `Correo_FE`. Con `||`: vacío ahí no dice nada.
    E_Mail:
      apiObj.EXCCRIElectronicInvoiceEmail ||
      apiObj.Correo_FE ||
      (apiObj.SellToEmail ?? apiObj.BillToContactEmail ?? apiObj.E_Mail ?? ""),
    Telefono:
      apiObj.Sell_to_Phone ??
      apiObj.SellToPhoneNo ??
      apiObj.Phone_No ??
      apiObj.PhoneNo ??
      apiObj.Telefono ??
      "",
    Mobil: apiObj.SellToMobilePhoneNo ?? apiObj.MobilePhoneNo ?? apiObj.Mobil ?? "",
    caja: apiObj.Caja ?? "",
    clientLocalId: "",
    turno: primerValor(apiObj, CLAVES.turno) ?? null,
    // El pull no escribe un campo que no sabe rellenar: vacio escrito encima es
    // indistinguible de vacio de verdad, y era exactamente lo que pasaba aqui.
    // Con `?? undefined` Dexie no toca el campo cuando BC no lo publica.
    Dto: apiObj.Dto ?? undefined,
    importeDto: apiObj.importeDto ?? undefined,
    Campaign_No_: primerValor(apiObj, CLAVES.campana) ?? "",
    Cupon: apiObj.Cupon ?? apiObj.Cod_Cupon ?? "",
    Tipo_Detalle: apiObj.Tipo_Detalle ?? "",
    No_Detalle: apiObj.No_Detalle ?? "",
    Payment_Method: apiObj.Payment_Method_Code ?? "",
    Establecimiento_Factura: apiObj.Establecimiento_Factura ?? "",
    Punto_de_Emision_Factura: apiObj.Punto_de_Emision_Factura ?? "",
    No_Comprobante_Fiscal_Rel: comprobanteRelacionado(apiObj) ?? "",
    No_Serie_NCF_Facturas: apiObj.No_Serie_NCF_Facturas ?? "",
    No_Serie_NCF_Abonos: apiObj.No_Serie_NCF_Abonos ?? "",
    Salesperson_Code: apiObj.Salesperson_Code ?? "",
    // Quien atendio la venta. Business Central lo publica en `ID_Cajero` y el
    // mapeo lo descartaba, asi que los 40.092 documentos que conservan un
    // cajero de verdad --de 2019 a 2021-- lo perdian al traerlos.
    ID_Cajero: cajeroDelDocumentoBC(apiObj) ?? "",
    // Tiquete o factura. Business Central lo publica con su nombre del
    // localizado --`EXCCRIElectronicDocumentType`-- y en el terminal el campo
    // se llama como la clave que se le manda. Sin esta traduccion, todo lo
    // traido se leia «Factura» aunque en BC fuera un tiquete.
    //
    // `?? undefined` y no `?? ""`: la pagina de borradores no publica el
    // campo, y escribir vacio encima borraria el tipo que el cajero eligio en
    // un documento que todavia no se ha registrado.
    // En una factura es lo que el documento ES; en un abono, lo que CORRIGE
    // --que es como lo usa el listado, y como ya lo hereda el abono creado en
    // el terminal--. Las paginas de abono no publican el primero pero si el
    // segundo, asi que sin esta linea un abono traido no se distingue del que
    // devuelve una factura.
    Electronic_Document_Type: tipoDeComprobanteDeBC(apiObj, countryCode),
    // Panamá: el CUFE y lo demás que la DGI devuelve al autorizar. Ver
    // `panama/datosFiscalesDePanama.js`.
    ...(countryCode === COUNTRY_CODES.PANAMA ? camposFiscalesDelDocumento(apiObj) : {}),
    // La hora del alta. Se leia para componer `lastModified` y se tiraba, asi
    // que el tiquete de un documento traido no la podia imprimir.
    Hora_creacion: horaLocalDelDocumento(apiObj, corte),
    // El enlace del abono con su factura. Sin el, `getCreditMemosForInvoice`
    // no encuentra los abonos previos de una factura traida y el terminal deja
    // devolver otra vez lo ya devuelto.
    //
    // Costa Rica todavia no escribe `Applies_to_Doc_No` en la importacion, asi
    // que el enlace se recupera del comprobante relacionado.
    //
    // Solo en los abonos, por el mismo motivo que en `mapPostedInvoice`.
    Applies_to_Doc_No: documentoAlQueSeAplica(apiObj, {
      conRespaldo: esUnAbono(apiObj.Document_Type, apiObj),
    }).No,
    Applies_to_Doc_Type: documentoAlQueSeAplica(apiObj, {
      conRespaldo: esUnAbono(apiObj.Document_Type, apiObj),
    }).Type,
    RTN: apiObj.VAT_Registration_No ?? "",
    // Campos para Perú (Alumno)
    Student_Grade: apiObj.Student_Grade ?? "",
    Student_Name: apiObj.Student_Name ?? "",
    Student_DNI: apiObj.Student_DNI ?? "",
    Student_Phone: apiObj.Student_Phone ?? "",
    // Campos para Perú (Nota de Crédito)
    ...camposDeLaNotaDeCredito(apiObj),
    // Perú: "01" factura, "03" boleta. Es lo único que distingue una boleta
    // traída de BC, y la nota de crédito que la corrija lo necesita para su
    // `SUNAT_Document_Ref`. Sólo si viene: vacío no pisa el que ya hubiera.
    ...(apiObj.SUNAT_Document ? { SUNAT_Document: apiObj.SUNAT_Document } : {}),
    pendingSync: 0,
    Posting_Date: apiObj.Posting_Date ?? "",
    lastModified: fechaHoraDelDocumento(apiObj, corte),
    lastSynced: new Date().toISOString(),
    deleted: 0,
    syncing: 0,
    syncStartTime: null,
    syncStage: "payments",
    posted: 0,
  };
};
export const mapPostedInvoice = (apiObj, documentType, countryCode, corte = null) => {
  return {
    systemId: apiObj.systemId ?? apiObj.SystemId ?? "",
    No: apiObj.No ?? "",
    // Vacio hasta que Business Central publique el campo en la query de
    // registradas; en cuanto lo haga, el guardia de idempotencia vuelve a
    // reconocer sus propios documentos despues de traer historicos.
    Id_TPV: apiObj.Id_TPV ?? "",
    Document_Type: tipoLocalDeLaVenta(apiObj.Document_Type || documentType, apiObj, countryCode),
    // Ver el mismo campo en `mapInvoice`.
    Tipo_SUNAT: apiObj.SUNAT_Document || undefined,
    Sell_to_Customer_No_: apiObj.Sell_to_Customer_No ?? "",
    Sell_to_Customer_Name_2: apiObj.Sell_to_Customer_Name_2 ?? "",
    // `undefined` y no cadena vacía, igual que `Dto` e `importeDto`.
    //
    // Que el campo llegue o no depende de la extensión que tenga instalada el
    // ambiente, no del país. Lo declara `ExceliaOne BC - E2` --campo 75200 en
    // `Sales Header`, `Sales Invoice Header` y `Sales Cr.Memo Header`-- y lo
    // publica en `FacturasAPI`, `PostedSalesInvoices` y
    // `PostedSalesCreditMemos`. Donde corre el núcleo `ExceliaOne BC` el campo
    // no existe, y entonces las cuatro entidades no lo publican: medido el
    // 16/10/2026 en Corporativo_Santillana_2_Dev, que es Costa Rica, Panamá y
    // Puerto Rico.
    //
    // En ese segundo caso `?? ""` lo BORRABA en cada fila que reemplaza Traer
    // Históricos, y lo borraba de verdad: lo teclea el cajero --formulario de El
    // Salvador y de Honduras, donde puede ser obligatorio-- y el ambiente no
    // tiene de dónde devolverlo. Con `undefined` la fila conserva la suya.
    //
    // Sí viaja a Business Central: `invoiceSyncService` arma la cabecera con
    // `...invoice` y no está en la lista de `camposLocalesDeLaFactura`. Donde
    // está E2 se guarda --`Cod90100` lo lee de `Razon_Social`-- y sobrevive al
    // contabilizar porque es el mismo número de campo en las tres tablas.
    Razon_Social: apiObj.Razon_Social ?? undefined,
    Posting_No: apiObj.External_Document_No ?? "",
    draftNo: "",
    Colegio: apiObj.Cod_Colegio ?? "",
    Bill_to_Name: apiObj.Bill_to_Name ?? "",
    Bill_to_Address: apiObj.Bill_to_Address ?? "",
    Direccion_2: mapDireccion2FromApi(apiObj),
    Bill_to_City: apiObj.Bill_to_City ?? "",
    VAT_Registration_No_: apiObj.VAT_Registration_No ?? "",
    Bill_to_Country_Region_Code: apiObj.Bill_to_Country_Region_Code ?? "",
    // La ubicación del comprador en Costa Rica. Con `?? undefined`: donde BC no
    // la publique, no se pisa la copia local. Contrato de ubicación, fila T6.
    ...(countryCode === COUNTRY_CODES.COSTARICA
      ? {
          Sell_to_Post_Code: apiObj.Sell_to_Post_Code ?? undefined,
          Sell_to_Country_Region_Code: apiObj.Sell_to_Country_Region_Code ?? undefined,
        }
      : {}),
    Ship_to_Code: apiObj.Ship_to_Code ?? "",
    Tipo_Documento_SrI: resolverTipoDeDocumento(apiObj, countryCode, "factura"),
    // En Costa Rica, primero el correo del comprobante (`E-Mail-FE`): las
    // registradas lo publican como `EXCCRIElectronicInvoiceEmail` y los abonos sin
    // registrar como `Correo_FE`. Con `||`: vacío ahí no dice nada.
    E_Mail:
      apiObj.EXCCRIElectronicInvoiceEmail ||
      apiObj.Correo_FE ||
      (apiObj.SellToEmail ?? apiObj.BillToContactEmail ?? apiObj.E_Mail ?? ""),
    Telefono:
      apiObj.Sell_to_Phone ??
      apiObj.SellToPhoneNo ??
      apiObj.Phone_No ??
      apiObj.PhoneNo ??
      apiObj.Telefono ??
      "",
    Mobil: apiObj.SellToMobilePhoneNo ?? apiObj.MobilePhoneNo ?? apiObj.Mobil ?? "",
    caja: apiObj.Caja ?? apiObj.TPV ?? "",
    clientLocalId: "",
    turno: primerValor(apiObj, CLAVES.turno) ?? null,
    // El pull no escribe un campo que no sabe rellenar: vacio escrito encima es
    // indistinguible de vacio de verdad, y era exactamente lo que pasaba aqui.
    // Con `?? undefined` Dexie no toca el campo cuando BC no lo publica.
    Dto: apiObj.Dto ?? undefined,
    importeDto: apiObj.importeDto ?? undefined,
    Cupon: apiObj.Cupon ?? apiObj.Cupon_de_descuento ?? apiObj.Cod_Cupon ?? "",
    Tipo_Detalle: apiObj.Tipo_Detalle ?? "",
    No_Detalle: apiObj.No_Detalle ?? "",
    Payment_Method: apiObj.Payment_Method_Code ?? "",
    Pre_Assigned_No: apiObj.Pre_Assigned_No ?? "",
    Establecimiento_Factura: apiObj.Establecimiento_Factura ?? "",
    Punto_de_Emision_Factura: apiObj.Punto_de_Emision_Factura ?? "",
    No_Serie_NCF_Facturas: apiObj.No_Serie_NCF_Facturas ?? "",
    No_Serie_NCF_Abonos: apiObj.No_Serie_NCF_Abonos ?? "",
    No_Comprobante_Fiscal: apiObj.No_Comprobante_Fiscal ?? "",
    No_Autorizacion_CAI: apiObj.No_Autorizacion_CAI ?? "",
    Campaign_No_: primerValor(apiObj, CLAVES.campana) ?? "",
    Salesperson_Code: apiObj.Salesperson_Code ?? "",
    // Quien atendio la venta. Business Central lo publica en `ID_Cajero` y el
    // mapeo lo descartaba, asi que los 40.092 documentos que conservan un
    // cajero de verdad --de 2019 a 2021-- lo perdian al traerlos.
    ID_Cajero: cajeroDelDocumentoBC(apiObj) ?? "",
    // Tiquete o factura. Business Central lo publica con su nombre del
    // localizado --`EXCCRIElectronicDocumentType`-- y en el terminal el campo
    // se llama como la clave que se le manda. Sin esta traduccion, todo lo
    // traido se leia «Factura» aunque en BC fuera un tiquete.
    //
    // `?? undefined` y no `?? ""`: la pagina de borradores no publica el
    // campo, y escribir vacio encima borraria el tipo que el cajero eligio en
    // un documento que todavia no se ha registrado.
    // En una factura es lo que el documento ES; en un abono, lo que CORRIGE
    // --que es como lo usa el listado, y como ya lo hereda el abono creado en
    // el terminal--. Las paginas de abono no publican el primero pero si el
    // segundo, asi que sin esta linea un abono traido no se distingue del que
    // devuelve una factura.
    Electronic_Document_Type: tipoDeComprobanteDeBC(apiObj, countryCode),
    // Panamá: el CUFE y lo demás que la DGI devuelve al autorizar. Ver
    // `panama/datosFiscalesDePanama.js`.
    ...(countryCode === COUNTRY_CODES.PANAMA ? camposFiscalesDelDocumento(apiObj) : {}),
    // La hora del alta. Se leia para componer `lastModified` y se tiraba, asi
    // que el tiquete de un documento traido no la podia imprimir.
    Hora_creacion: horaLocalDelDocumento(apiObj, corte),
    // El enlace del abono con su factura. Sin el, `getCreditMemosForInvoice`
    // no encuentra los abonos previos de una factura traida y el terminal deja
    // devolver otra vez lo ya devuelto.
    //
    // **Solo en los abonos.** Una factura no «se aplica a» otro documento, y los
    // campos de respaldo vienen rellenos tambien en ella --`No. Doc Historico`
    // lleva SU propio numero del sistema anterior, no una referencia a otro
    // documento--. Sin esta condicion, cada factura traida quedaba apuntandose a
    // si misma.
    // Se escribe vacío y no se omite: omitirlo dejaría intacto un enlace erróneo
    // que un pull anterior hubiera puesto, y limpiarlo es justo lo que hace
    // falta. Vacío acá es una afirmación --«esta factura no corrige nada»--, no
    // un «no sé».
    Applies_to_Doc_No: documentoAlQueSeAplica(apiObj, {
      conRespaldo: esUnAbono(documentType, apiObj),
    }).No,
    Applies_to_Doc_Type: documentoAlQueSeAplica(apiObj, {
      conRespaldo: esUnAbono(documentType, apiObj),
    }).Type,
    No_Comprobante_Fiscal_Rel: esUnAbono(documentType, apiObj)
      ? (comprobanteRelacionado(apiObj) ?? "")
      : "",
    RTN: apiObj.VAT_Registration_No ?? "",
    // Campos para Perú (Alumno)
    Student_Grade: apiObj.Student_Grade ?? "",
    Student_Name: apiObj.Student_Name ?? "",
    Student_DNI: apiObj.Student_DNI ?? "",
    Student_Phone: apiObj.Student_Phone ?? "",
    // Campos para Perú (Nota de Crédito)
    ...camposDeLaNotaDeCredito(apiObj),
    // Ver `mapInvoice`.
    ...(apiObj.SUNAT_Document ? { SUNAT_Document: apiObj.SUNAT_Document } : {}),
    pendingSync: 0,
    Posting_Date: apiObj.Posting_Date ?? apiObj.Document_Date ?? "",
    lastModified: fechaHoraDelDocumento(apiObj, corte),
    lastSynced: new Date().toISOString(),
    deleted: 0,
    syncing: 0,
    syncStartTime: null,
    syncStage: "posted",
    posted: 1,
    // Los seis motivos por los que un documento registrado puede no admitir
    // nota de crédito. No se persistía ninguno, así que la exclusión no se
    // podía aplicar aunque Business Central la publicara: el listado los
    // conocía al traerlos y los tiraba.
    ...camposDeExclusion(apiObj),
  };
};

/**
 * Lo que los mapeadores de líneas necesitan de la base local y no viene en la
 * línea. Se arma una vez por lote con `contextoParaMapearLineas`.
 *
 * @typedef {object} ContextoDeLineas
 * @property {Map<string, number>} [tasasDeIva] `VAT_Percent` por
 *   `claveDeTasaDeIva(grupoNegocio, grupoProducto)`.
 * @property {string} [cuentaDeBolsas] La cuenta del ICBPER (`Account_Tax_Bag`).
 */

export const claveDeTasaDeIva = (grupoNegocio, grupoProducto) =>
  `${grupoNegocio ?? ""}|${grupoProducto ?? ""}`;

/**
 * El porcentaje de descuento de la línea.
 *
 * El que publica Business Central (`Line_Discount_Percent`) y, sólo si falta,
 * el que se reconstruye de los importes. Reconstruirlo no es exacto: BC ya
 * redondeó `Line_Discount_Amount`, así que 45,90 al 31 % vuelve como 30,9967 %,
 * y al rehacer la cuenta con ese porcentaje el importe de la línea se corre un
 * centavo.
 */
const porcentajeDeDescuento = (apiResponse, reconstruido) => {
  const publicado = Number(apiResponse.Line_Discount_Percent);
  return apiResponse.Line_Discount_Percent != null && Number.isFinite(publicado)
    ? publicado
    : reconstruido;
};

/**
 * El porcentaje de IVA de la configuración local para los grupos de la línea,
 * o `undefined` si no hay contexto o la combinación no está configurada.
 *
 * Es el paso intermedio cuando la entidad no publica `VAT_Percent`: derivarlo de
 * `Amount_Including_VAT - Line_Amount` sale de importes ya redondeados, y una
 * bolsa de 0,40 al 18 % (0,472 → 0,47) vuelve como 17,5 %.
 */
const tasaDeIvaConfigurada = (apiResponse, contexto) =>
  contexto?.tasasDeIva?.get(
    claveDeTasaDeIva(apiResponse.VAT_Bus_Posting_Group, apiResponse.VAT_Prod_Posting_Group)
  );

/**
 * Los campos que convierten la línea del ICBPER que manda Business Central en
 * la fila de impuesto que pinta la app (`esImpuestoDeBolsa`, ver
 * utils/impuestoDeBolsas.js).
 *
 * En una venta local el impuesto no es una línea: se deriva del producto. Pero
 * BC sí lo guarda como una línea de cuenta, y al traerlo de históricos se sumaba
 * al subtotal como un producto más y desaparecía la fila «ICBPER».
 */
const camposDeImpuestoDeBolsaDeBC = (apiResponse, contexto) => {
  const cuenta = contexto?.cuentaDeBolsas;
  if (!cuenta || apiResponse.Type !== "G/L Account" || apiResponse.No !== cuenta) return {};
  return {
    esImpuestoDeBolsa: true,
    importe: Number(apiResponse.Amount_Including_VAT ?? apiResponse.Line_Amount) || 0,
  };
};

/**
 * Las tasas de IVA y la cuenta del ICBPER guardadas, para pasarlas a los
 * mapeadores de líneas. Sin configuración sincronizada devuelve un contexto
 * vacío y los mapeadores se portan como antes.
 *
 * @returns {Promise<ContextoDeLineas>}
 */
export const contextoParaMapearLineas = async () => {
  const [grupos, conf] = await Promise.all([
    db.grupoIVA.toArray().catch(() => []),
    db.confContabilidad
      .toCollection()
      .first()
      .catch(() => null),
  ]);

  const tasasDeIva = new Map(
    (grupos || []).map((g) => [
      claveDeTasaDeIva(g.VAT_Bus_Posting_Group, g.VAT_Prod_Posting_Group),
      Number(g.VAT_Percent) || 0,
    ])
  );

  return { tasasDeIva, cuentaDeBolsas: conf?.Account_Tax_Bag || "" };
};

// Mapping: invoices lines + credit memos lines + posted invoice lines
//
// `contexto` es opcional (ver `contextoParaMapearLineas`). Ojo al pasar el
// mapeador a un `.map`: el segundo argumento sería el índice, así que se
// envuelve: `(l) => mapLine(l, contexto)`.
export const mapLine = (apiResponse, contexto = null) => {
  const lineAmount = apiResponse.Line_Amount ?? apiResponse.Amount ?? 0;

  const discountAmount = apiResponse.Line_Discount_Amount ?? 0;

  // Ignorar líneas que no son de negocio (comentarios, vacías, etc.)
  const isCommentLine = apiResponse.Type === " " || apiResponse.FilteredTypeField === "Comment";

  // VAT
  let vatAmount = 0;

  if (!isCommentLine) {
    if (apiResponse.Total_VAT_Amount != null) {
      vatAmount = apiResponse.Total_VAT_Amount;
    } else if (apiResponse.Amount_Including_VAT != null && lineAmount != null) {
      vatAmount = apiResponse.Amount_Including_VAT - lineAmount;
    }
  }

  // Descuento %
  // En magnitud: estas funciones mapean también **abonos**, cuyos importes
  // bajan de Business Central en negativo. Con la comparación a secas el
  // porcentaje se reconstruía en 0, y el abono quedaba guardado sin IVA y sin
  // descuento --sin error y sin nada que mirar--.
  const brutoDeLaLinea = Math.abs(lineAmount + discountAmount);
  const discountPercent = porcentajeDeDescuento(
    apiResponse,
    brutoDeLaLinea > 0 ? (discountAmount * 100) / (lineAmount + discountAmount) : 0
  );

  // IVA %, por la misma razón que el descuento de arriba.
  let calculatedPorIva = Math.abs(lineAmount) > 0 ? (vatAmount * 100) / lineAmount : 0;

  const retencion = apiResponse.Retention_VAT_Percent;

  return {
    invoiceNo: apiResponse.Document_No,
    Line_No: apiResponse.Line_No,
    Type: apiResponse.Type,
    No: apiResponse.No,
    Description: apiResponse.Description,
    Quantity: apiResponse.Quantity,
    Unit_Price: apiResponse.Unit_Price,

    Line_Discount_: discountPercent,
    Line_Amount: lineAmount,

    // La retención sólo manda cuando de verdad hay retención. Business Central
    // publica Retention_VAT_Percent en 0 en varias entidades, y como `??` sólo
    // se salta null/undefined, ese 0 le ganaba al IVA real y la línea quedaba
    // exenta: una nota de crédito de 229,02 se mostraba en 217,07. Con
    // retención en 0 la respuesta correcta es la misma por cualquiera de las
    // dos vías, así que preferir el IVA no cambia ningún caso legítimo.
    PorIva: isCommentLine
      ? 0
      : ((retencion || apiResponse.VAT_Percent) ??
        tasaDeIvaConfigurada(apiResponse, contexto) ??
        calculatedPorIva),

    Total_VAT_Amount: vatAmount,

    // El importe de la línea con IVA tal como lo tiene Business Central.
    //
    // Se guarda porque es el único valor que cuadra contra el pago: rehacer la
    // cuenta desde Unit_Price x Cantidad x (1-dto%) x (1+iva%) redondea cada
    // línea por separado y se desvía un centavo cada vez que cae en el medio
    // (39,58 al 15% da 45,517; BC guardó 45,51). En una factura de seis líneas
    // eso suma 0,03 y el Pendiente se sale de la tolerancia de 0,02 sin que
    // nada esté mal. Ver calculateLineTotals en utils/invoiceTotals.js.
    Amount_Including_VAT: apiResponse.Amount_Including_VAT ?? null,

    ...camposDeImpuestoDeBolsaDeBC(apiResponse, contexto),

    pendingSync: 0,
    lastModified: new Date().toISOString(),
    lastSynced: new Date().toISOString(),
    deleted: 0,
  };
};

// Mapping: posted credit memos y facturas sin contabilizar
export const mapLineCalculateVat = (apiResponse, contexto = null) => {
  const lineAmount = apiResponse.Line_Amount ?? 0; // sin IVA
  const discountAmount = apiResponse.Line_Discount_Amount ?? 0;
  const amountIncludingVat = apiResponse.Amount_Including_VAT ?? 0;

  // Calcular porcentaje de descuento real (si Line_Amount ya está descontado)
  // En magnitud: estas funciones mapean también **abonos**, cuyos importes
  // bajan de Business Central en negativo. Con la comparación a secas el
  // porcentaje se reconstruía en 0, y el abono quedaba guardado sin IVA y sin
  // descuento --sin error y sin nada que mirar--.
  const brutoDeLaLinea = Math.abs(lineAmount + discountAmount);
  const discountPercent = porcentajeDeDescuento(
    apiResponse,
    brutoDeLaLinea > 0 ? (discountAmount * 100) / (lineAmount + discountAmount) : 0
  );

  // El porcentaje que publica Business Central; si no lo publica, el de la
  // configuración local para los grupos de la línea, y sólo en último caso el
  // que se deriva de la resta.
  //
  // Hasta el bloque 2 del acuerdo 11, las tres entidades que no eran
  // `LinesPostedSalesInvoice` no publicaban `VAT_Percent`, así que no quedaba
  // más remedio que derivarlo. Derivar funciona pero no es exacto: 62,44 al 15%
  // da 15,0064 y no 15, y con base cero da 0 aunque la línea tenga tasa. Ahora
  // las cuatro lo publican --comprobado en el ambiente-- y se usa el dato.
  const derivado =
    Math.abs(lineAmount) > 0 ? ((amountIncludingVat - lineAmount) * 100) / lineAmount : 0;
  //
  // `LinesSalesInvoice` de Perú resultó no publicarlo (medido en
  // Santillana_Peru_Test, VFAC18-01124310): la bolsa de 0,40 → 0,47 quedaba en
  // 17,5 % en vez de 18 %.
  const calculatedPorIva =
    apiResponse.VAT_Percent ?? tasaDeIvaConfigurada(apiResponse, contexto) ?? derivado;

  let result = {
    invoiceNo: apiResponse.Document_No,
    Line_No: apiResponse.Line_No,
    Type: apiResponse.Type,
    No: apiResponse.No,
    Description: apiResponse.Description,
    Quantity: apiResponse.Quantity,
    Unit_Price: apiResponse.Unit_Price, // precio base sin IVA
    Line_Discount_: discountPercent, // % de descuento (lo que tu cálculo espera)
    Line_Amount: lineAmount, // sin IVA
    PorIva: calculatedPorIva,
    // Mismo motivo que en mapLine: es el importe que cuadra contra el pago.
    Amount_Including_VAT: apiResponse.Amount_Including_VAT ?? null,
    ...camposDeImpuestoDeBolsaDeBC(apiResponse, contexto),
    pendingSync: 0,
    lastModified: new Date().toISOString(),
    lastSynced: new Date().toISOString(),
    deleted: 0,
  };

  return result;
};

//Mapping: Payment details invoice + payment details credit memos
export function mapPaymentDetails(bcObj) {
  const baseAmount = parseFloat(bcObj?.Importe ?? 0);
  const discount = parseFloat(bcObj?.Descuento ?? 0);
  // En magnitud: el detalle de pago de un abono baja con el importe en
  // negativo, y el descuento se guardaba en 0.
  const discountPercentage = Math.abs(baseAmount) > 0 ? (discount * 100) / baseAmount : 0;

  return {
    invoiceId: 0, // No existe en BC
    Document_Type: bcObj?.Document_Type || "Invoice",
    Document_No_: bcObj?.Document_No_ || "",
    Line_No_: bcObj?.Line_No_ || 1,
    Payment_Method: bcObj?.Forma_de_Pago || "EFECTIVO",
    Amount: baseAmount,
    Dto: parseFloat(discountPercentage.toFixed(2)),
    Dto_Amount: discount,

    // La factura contabilizada a la que pertenece de verdad este pago.
    //
    // DetallePagoFactura se indexa por Document_No_, que es el número de
    // borrador -- y ese número NO identifica una sola factura contabilizada:
    // en Ecuador conviven dos series de contabilizadas (PVMDSC3R-000000001 de
    // nueve dígitos y PVMDSC3R-000001 de seis) que comparten el mismo
    // Pre_Assigned_No. Medido contra el ambiente real: 3.174 pares lo
    // comparten, y en Mall del Sol eso hacía que el 45% de las facturas de una
    // caja mostrara el pago de otra (PVMDSC4R-000461, de 36,90, mostraba los
    // 641,52 de PVMDSC4R-000004003).
    //
    // Business Central manda la respuesta en la misma fila: este campo trae el
    // número definitivo. No se puede filtrar en el servidor --BC lo marca como
    // no filtrable-- pero sí descartar al mapear y al leer.
    Documento_Definitivo: bcObj?.Documento_Definitivo || "",
    deleted: false,
    lastModified: new Date().toISOString(),
    lastSynced: new Date().toISOString(),
    pendingSync: 0,
  };
}

/**
 * Descarta los pagos que Business Central atribuye a otra factura contabilizada.
 *
 * Los pagos se piden por `Document_No_ eq <Pre_Assigned_No>`, y ese número lo
 * pueden compartir dos facturas contabilizadas distintas (ver el comentario de
 * `Documento_Definitivo` en mapPaymentDetails). Cuando eso pasa, el lote trae
 * pagos que no son de ninguno de los documentos que se están importando, o que
 * son de uno solo de los dos.
 *
 * Se conserva la fila si:
 *  - no trae `Documento_Definitivo` (la factura sigue sin contabilizar, así que
 *    el número de borrador todavía la identifica sin ambigüedad), o
 *  - su `Documento_Definitivo` es el de alguno de los documentos del lote.
 *
 * Lo que no se puede resolver acá es cuál de dos hermanas del mismo lote se lo
 * queda: eso lo decide la lectura, en invoicePaymentService.
 *
 * @param {Array} payments   Pagos ya mapeados con mapPaymentDetails.
 * @param {Array} documents  Documentos contabilizados del lote (usan `No`).
 * @returns {Array} Los pagos atribuibles a alguno de esos documentos.
 */
export function descartarPagosDeOtroDocumento(payments = [], documents = []) {
  const propios = new Set((documents || []).map((d) => d?.No).filter(Boolean));
  if (!propios.size) return payments || [];

  return (payments || []).filter(
    (p) => !p?.Documento_Definitivo || propios.has(p.Documento_Definitivo)
  );
}

// Excluye documentos con importe 0 Y sin formas de pago (aplica a todos los países).
//
// Y además, desde el acuerdo `contrato-documento-borrado-y-pagos-huerfanos`, los
// documentos con forma de sello: registrados, con líneas que suman cero y pagos
// que no. Ésos entraban **precisamente porque tenían pagos** --la condición de
// arriba los salvaba-- y se traían el dinero huérfano al arqueo del cierre.
//
// Es la red para Honduras y El Salvador, donde el campo `DocumentoBorradoTPV`
// no llega porque su entidad registrada va por otro endpoint, y para cualquier
// ambiente que todavía no tenga la 1.0.4.96. Donde el campo sí llega, el sello
// ya se apartó antes de pedir líneas y pagos y esto no lo vuelve a ver.
export function filterEmptyDocuments({ documents, lines, payments, getLineKey, getPaymentKey }) {
  const amounts = lines.reduce((acc, l) => {
    if (l?.invoiceNo)
      acc.set(l.invoiceNo, (acc.get(l.invoiceNo) || 0) + (Number(l.Line_Amount) || 0));
    return acc;
  }, new Map());
  const paid = new Set(payments.map((p) => p?.Document_No_).filter(Boolean));
  // `Amount`, no `Importe`. Aquí los pagos llegan ya mapeados por
  // `mapPaymentDetails`, que renombra el `Importe` crudo de Business Central a
  // `Amount` --el nombre con el que vive en la base local--. Leer `Importe`
  // daba `undefined` en todas las filas, así que este mapa valía cero entero y
  // la regla estructural de `tieneFormaDeSello` no saltaba nunca: la red de
  // Honduras y El Salvador, y la de los ambientes sin la 1.0.4.96, estaba
  // muerta sin que nada lo dijera.
  const cobrado = payments.reduce((acc, p) => {
    if (p?.Document_No_)
      acc.set(p.Document_No_, (acc.get(p.Document_No_) || 0) + (Number(p.Amount) || 0));
    return acc;
  }, new Map());

  const keepL = new Set(),
    keepP = new Set();
  const apartados = [];
  const docs = documents.filter((d) => {
    const lk = getLineKey(d),
      pk = getPaymentKey(d);
    if (Math.abs(amounts.get(lk) || 0) < 0.01 && (!pk || !paid.has(pk))) return false;
    if (tieneFormaDeSello(d?.posted === 1, amounts.get(lk) || 0, cobrado.get(pk) || 0)) {
      apartados.push(`${pk || lk} (pagos ${(cobrado.get(pk) || 0).toFixed(2)}, líneas 0)`);
      return false;
    }
    if (lk) keepL.add(lk);
    if (pk) keepP.add(pk);
    return true;
  });

  // Se anota porque un documento que desaparece sin decirlo es la clase de
  // silencio que este proyecto lleva catalogando desde el caso 01.
  if (apartados.length) {
    anotarAviso(
      "facturas",
      `documentos borrados descartados al traer históricos: ${apartados.join(", ")}`
    );
  }

  return {
    documents: docs,
    lines: lines.filter((l) => keepL.has(l?.invoiceNo)),
    payments: payments.filter((p) => keepP.has(p?.Document_No_)),
  };
}

// Chunk logic -> crea lotes de documentos para hacer multiples llamadas (Una sola = Network error)
export function chunkArray(array, size) {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

export async function fetchLinesInChunks(
  endpoint,
  documents,
  mapFn,
  chunkSize = 35,
  filterDocumentLabel = "Document_No",
  usePreAssigned = false
) {
  const chunks = chunkArray(documents, chunkSize);
  let allResults = [];

  for (const chunk of chunks) {
    const filter = chunk
      .map((doc) => {
        const value = usePreAssigned ? doc.Pre_Assigned_No : doc.No;
        return `${filterDocumentLabel} eq '${value}'`;
      })
      .join(" or ");

    const response = await fetchFromBusinessCentral(`${endpoint}?$filter=${filter}`);

    allResults.push(...response.map(mapFn));
  }

  return allResults;
}

// Mapea respuestas en formato que espera el componente Select de Antd
export const mapApiOptionsResponse = (apiResponse) => {
  if (!apiResponse || !Array.isArray(apiResponse)) {
    return [];
  }

  return apiResponse.map((item) => {
    // Detectar el campo de ID según la API
    const id =
      item["Cód_Departamento_FE"] ||
      item["Cód_Distrito_FE"] ||
      item["Cód_Ciudad_FE"] ||
      item["Code"] ||
      null;

    // Detectar el campo de descripción o nombre
    const label = item["Descripción"] || item["Description"] || item["Name"] || "";

    return {
      label,
      value: id,
    };
  });
};

// Función para emitir eventos de sincronización
export const emitSyncEvent = (message, isComplete = false) => {
  const event = new CustomEvent("syncStatus", {
    detail: { message, isComplete },
  });
  window.dispatchEvent(event);
};

/**
 * Emite el resumen de fallos parciales de una sincronización: los registros que
 * Business Central rechazó aunque el proceso completo terminara bien.
 *
 * Va por un canal aparte de emitSyncEvent a propósito. "syncStatus" es un
 * indicador de una sola línea en el que cada paso pisa el mensaje del anterior,
 * y en el bloque de catálogos, que corre en paralelo, un motivo de rechazo
 * duraba milisegundos: era materialmente imposible de leer. Los fallos se
 * juntan durante toda la corrida y se cuentan una sola vez al final, en un
 * panel que no se va solo. Ver SANTPV-59.
 *
 * @param {Array<{ operationName: string, message: string }>} failures
 *   Lista vacía para limpiar el panel al arrancar una corrida nueva.
 */
export const emitSyncFailuresEvent = (failures) => {
  window.dispatchEvent(new CustomEvent("syncFailures", { detail: { failures } }));
};
